import { Route, Routes, Navigate } from "react-router-dom"
import AuthGuard from "../../components/auth/auth-guard"
import { AuthProvider } from "../../context/auth-context"
import { ServiceProvider } from "../../context/service-context"
import { SocketProvider } from "../../context/socket-context" // Import from correct file
import AuthLayout from "../../components/layout/auth-layout"
import CareerLayout from "../../components/layout/career-layout"
import NotFound from "../../pages/not-found"

// Import Pages
import DashboardHome from "../../pages/candidate-dashboard/dashboard-home"
import FinalTest from "../../pages/candidate-dashboard/final-test"
import InitialTest from "../../pages/candidate-dashboard/initial-test"
import Training from "../../pages/candidate-dashboard/training"
import SuccessPage from "../../pages/career/success-page"
import Services from "../../pages/services/services-home"
import { CandidateLayout } from "../../components/layout/candidate-layout"
import GoodsLayout from "../../components/layout/goods-layout"
import PublicLayout from "../../components/layout/public-layout"
import ServicesLayout from "../../components/layout/services-layout"
import AboutUs from "../../pages/about-us"
import Login from "../../pages/authentication/login"
import Register from "../../pages/authentication/register"
import JobApplicationForm from "../../pages/career/job-apply"
import JobDetails from "../../pages/career/job-details"
import JobListing from "../../pages/career/job-listing"
import ContactUs from "../../pages/contact-us"
import Faqs from "../../pages/faqs"
import LandingPage from "../../pages/landing-page"
import Home from "../../pages/goods/home"
import CartPage from "../../pages/goods/cart-page"
import CheckoutPage from "../../pages/goods/checkout-page"
import OrderConfirmationPage from "../../pages/goods/order-confirmation-page"
import OrdersPage from "../../pages/goods/orders-page"
import ProductDetailPage from "../../pages/goods/product-detail-page"
import SubCategories from "../../pages/services/sub-categories"
import CustomerServiceRequest from "../../pages/services/customer-service-request"
import LiveHandymanTracking from "../../pages/services/live-handyman-tracking"
import ResultCard from "../../pages/candidate-dashboard/result"
import UploadDocumentsPage from "../../pages/candidate-dashboard/uploaddocuments"
import ResetPassword from "../../pages/authentication/reset-password"
import ForgotPassword from "../../pages/authentication/forgot-password"
import ChangePassword from "../../pages/authentication/change-password"
import VerifyOtp from "../../pages/authentication/verify-otp"

import EmployeeTasks from "../../pages/candidate-dashboard/employee-tasks"
import EmployeeDashboardPage from "../../pages/candidate-dashboard/employee-dashboard"
import HandymanOnboarding from "../../pages/handyman/handyman-onboarding"
import HandymanLayout from "../../components/layout/handyman-layout"
import HandymanDashboard from "../../pages/handyman/dashboard"
import HandymanTasks from "../../pages/handyman/tasks"
import HandymanMyJobs from "../../pages/handyman/my-jobs"
import HandymanSchedule from "../../pages/handyman/schedule"
import HandymanScheduleDetail from "../../pages/handyman/schedule-detail"

import CustomerLayout from "../../components/layout/customer-layout"
import CustomerDashboard from "../../pages/customer/dashboard"
import CustomerJobs from "../../pages/customer/jobs"
import CustomerSchedule from "../../pages/customer/schedule"
import CustomerScheduleDetail from "../../pages/customer/schedule-detail"
import CustomerJobDetail from "../../pages/customer/job-detail"

import AccountLayout from "../../components/layout/account-layout"
import SharedProfile from "../../components/common/shared-profile"

// Simple function to get current user (not a hook since it's used in component)
const getCurrentUser = () => {
  if (typeof window !== 'undefined') {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      try {
        return JSON.parse(authUser);
      } catch (error) {
        console.error("Error parsing authUser from localStorage:", error);
        return null;
      }
    }
  }
  return null;
};

const AppLayout = () => {
  const currentUser = getCurrentUser();

  return (
    <AuthProvider>
      <ServiceProvider>
        <SocketProvider>
          <Routes>
            {/* Public Layout */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/faqs" element={<Faqs />} />
              <Route path="/contact-us" element={<ContactUs />} />
            </Route>

            {/* Career Layout */}
            <Route element={<CareerLayout />}>
              <Route path="/career" element={<JobListing />} />
              <Route path="/job-details/:id" element={
                <AuthGuard requireAuth={false}>
                  <JobDetails />
                </AuthGuard>
              }
              />
              <Route
                path="/job-details/apply/:jobId"
                element={
                  <AuthGuard requireAuth={false}>
                    <JobApplicationForm />
                  </AuthGuard>
                }
              />
              <Route path="/success-page" element={<SuccessPage />} />
            </Route>

            {/* Unified Candidate & Employee Layout */}
            <Route element={<CandidateLayout />}>
              <Route
                path="/dashboard"
                element={<Navigate to="/emp/dashboard" replace />}
              />
              <Route path="/emp/dashboard" element={<EmployeeDashboardPage />} />
              <Route path="/emp/tasks" element={<EmployeeTasks />} />
              <Route
                path="/initial-test"
                element={
                  <AuthGuard requireAuth={false}>
                    <InitialTest />
                  </AuthGuard>
                }
              />
              <Route
                path="/training"
                element={
                  <AuthGuard requireAuth={false}>
                    <Training />
                  </AuthGuard>
                }
              />
              <Route
                path="/final-test"
                element={
                  <AuthGuard requireAuth={false}>
                    <FinalTest />
                  </AuthGuard>
                }
              />
              <Route
                path="/result"
                element={
                  <AuthGuard requireAuth={false}>
                    <ResultCard />
                  </AuthGuard>
                }
              />
              <Route
                path="/upload-documents"
                element={
                  <AuthGuard requireAuth={false}>
                    <UploadDocumentsPage />
                  </AuthGuard>
                }
              />
            </Route>

            {/* Authentication Layout */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/reset-password" element={<ResetPassword />} />
              <Route path="/auth/change-password" element={<ChangePassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-otp" element={<VerifyOtp />} />
            </Route>

            <Route path="/handyman-onboarding" element={<HandymanOnboarding />} />

            <Route element={<HandymanLayout />}>
              <Route path="/handyman/dashboard" element={<HandymanDashboard />} />
              <Route path="/handyman/tasks" element={<HandymanTasks />} />
              <Route path="/handyman/my-jobs" element={<HandymanMyJobs />} />
              <Route path="/handyman/schedule" element={<HandymanSchedule />} />
              <Route path="/handyman/schedule/:id" element={<HandymanScheduleDetail />} />
            </Route>

            <Route element={<GoodsLayout />}>
              <Route path="/goods" element={<Home />} />
              <Route path="/goods/product/:productId" element={<ProductDetailPage />} />
              <Route
                path="/goods/cart"
                element={
                  <AuthGuard requireAuth={false}>
                    <CartPage />
                  </AuthGuard>
                }
              />
              <Route
                path="/goods/checkout"
                element={
                  <AuthGuard requireAuth={false}>
                    <CheckoutPage />
                  </AuthGuard>
                }
              />
              <Route
                path="/goods/order-confirmation/:orderId"
                element={
                  <AuthGuard requireAuth={false}>
                    <OrderConfirmationPage />
                  </AuthGuard>
                }
              />
              <Route
                path="/goods/orders"
                element={
                  <AuthGuard requireAuth={false}>
                    <OrdersPage />
                  </AuthGuard>
                }
              />
            </Route>

            {/* Services Layout */}
            <Route element={<ServicesLayout />}>
              <Route path="/services" element={<Services />} />
              <Route path="/services/:id" element={<SubCategories />} />
              <Route
                path="/customer/service-request"
                element={
                  <AuthGuard requireAuth={false}>
                    <CustomerServiceRequest />
                  </AuthGuard>
                }
              />
              <Route
                path="/customer/service-request/live"
                element={
                  <AuthGuard requireAuth={false}>
                    <LiveHandymanTracking />
                  </AuthGuard>
                }
              />
            </Route>
            {/* Customer Dashboard Layout */}
            <Route element={<CustomerLayout />}>
              <Route path="/customer/dashboard" element={<CustomerDashboard />} />
              <Route path="/customer/jobs" element={<CustomerJobs />} />
              <Route path="/customer/jobs/:id" element={<CustomerJobDetail />} />
              <Route path="/customer/schedule" element={<CustomerSchedule />} />
              <Route path="/customer/schedule/:id" element={<CustomerScheduleDetail />} />
            </Route>

            {/* Unified Account Layout */}
            <Route element={<AccountLayout />}>
              <Route
                path="/account"
                element={
                  <AuthGuard requireAuth={true}>
                    <SharedProfile />
                  </AuthGuard>
                }
              />
            </Route>


            {/* Fallback Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </SocketProvider>
      </ServiceProvider>
    </AuthProvider>
  )
}

export default AppLayout